import createCachedSelector from 're-reselect';
import { get } from 'lodash-es';
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import {
  baseColumnsDefaultConfigs,
  defaultColumnConfigurations,
  allSearchResultColumnsConfigs,
} from '../../../store/orders/grid/default-column-configuration';
import { GridConfigService } from '../../../services/grid-config-service';
import type { OrdersGridColumnConfig } from './insurance-column-visibility';

import type {
  OrdersGridActiveGroupBy,
  OrdersGridGroupByCount,
  OrdersGridRateJob,
  OrdersGridState,
  OrdersGridViewProps,
} from './orders.grid.types';
import type { RootState } from 'store/root-state';

export const selectOrders = (state: OrdersGridState): string[] => get(state, 'orders', emptyArray as string[]);
export const selectPage = (state: OrdersGridState): number => get(state, 'page');
export const selectPageSize = (state: OrdersGridState): number => get(state, 'pageSize');
export const selectTotalRecords = (state: OrdersGridState): number | undefined => get(state, 'totalRecords');
export const selectTotalPages = (state: OrdersGridState): number => get(state, 'totalPages');
export const selectViewStartingCount = (state: OrdersGridState): number => get(state, 'viewStartingCount');
export const selectViewEndingCount = (state: OrdersGridState): number => get(state, 'viewEndingCount');
export const selectIsLoading = (state: OrdersGridState): boolean => get(state, 'isLoading');
export const selectIsGroupBysCountLoading = (state: OrdersGridState): boolean => get(state, 'isGroupBysCountLoading');
export const selectisSidebarVisible = (state: OrdersGridState): boolean => get(state, 'isSidebarVisible');
export const selectUserGridConfigurations = (state: OrdersGridState): OrdersGridState['userViewConfigurations'] =>
  get(state, 'userViewConfigurations', emptyObject);
export const selectOrderGridRateJobs = (state: OrdersGridState): OrdersGridRateJob[] =>
  get(state, 'rateJobs', emptyArray as OrdersGridRateJob[]);
export const selectAlternativeRateJobs = (state: OrdersGridState): OrdersGridRateJob[] =>
  get(state, 'alternativeRateJobs', emptyArray as OrdersGridRateJob[]);
export const selectActiveGroupByEnums = (state: OrdersGridState): OrdersGridActiveGroupBy[] =>
  get(state, 'activeGroupBys', emptyArray as OrdersGridActiveGroupBy[]);
export const selectActiveGroupByCount = (state: OrdersGridState): OrdersGridGroupByCount[] =>
  get(state, 'groupByCount', emptyArray as OrdersGridGroupByCount[]);
export const selectHasGetOrdersFailure = (state: OrdersGridState): boolean => get(state, 'hasGetOrdersFailure');

const mergeConfigurationsSelector: (
  viewConfiguration: OrdersGridColumnConfig[] | undefined,
  statusViewId: string,
  activeFilterViewId: string,
) => OrdersGridColumnConfig[] = createCachedSelector(
  (viewConfiguration: OrdersGridColumnConfig[] | undefined) => viewConfiguration,
  (viewConfiguration): OrdersGridColumnConfig[] =>
    GridConfigService.mergeUserAndDefaultConfigs(viewConfiguration, defaultColumnConfigurations),
)((_: OrdersGridColumnConfig[] | undefined, statusViewId: string, activeFilterViewId: string) => `${statusViewId}_${activeFilterViewId}`);

const selectCustomViewColumnConfiguration = (state: OrdersGridState): OrdersGridColumnConfig[] | undefined =>
  get(state, 'customViewColumnConfiguration');

export const selectCurrentViewGridConfigOrDefault = (
  state: OrdersGridState,
  { activeFilterViewId, statusViewId }: OrdersGridViewProps,
): OrdersGridColumnConfig[] => {
  if (activeFilterViewId === 'custom') {
    const viewConfiguration = selectCustomViewColumnConfiguration(state);
    return mergeConfigurationsSelector(viewConfiguration, statusViewId, activeFilterViewId);
  }

  const allGridConfigs = selectUserGridConfigurations(state);
  const { [activeFilterViewId]: configForView } = allGridConfigs[statusViewId] || {};

  if (!configForView) {
    return statusViewId?.includes('all-orders-search-result') ? allSearchResultColumnsConfigs : baseColumnsDefaultConfigs;
  }

  return mergeConfigurationsSelector(configForView, statusViewId, activeFilterViewId);
};

export const selectCurrentOrderByColumn = (
  state: OrdersGridState,
  { activeFilterViewId, statusViewId }: OrdersGridViewProps,
): OrdersGridColumnConfig | undefined => {
  const gridConfig =
    selectCurrentViewGridConfigOrDefault(state, {
      statusViewId,
      activeFilterViewId,
    }) || emptyArray;

  return gridConfig.find((col) => !!col.sorted);
};

export const selectOrdersGridOrders = (state: RootState): string[] => selectOrders(get(state, 'orders.grid'));
export const selectOrdersGridPage = (state: RootState): number => selectPage(get(state, 'orders.grid'));
export const selectOrdersGridPageSize = (state: RootState): number => selectPageSize(get(state, 'orders.grid'));
export const selectOrdersGridTotalRecords = (state: RootState): number | undefined => selectTotalRecords(get(state, 'orders.grid'));
export const selectOrdersGridTotalPages = (state: RootState): number => selectTotalPages(get(state, 'orders.grid'));
export const selectOrdersGridViewStartingCount = (state: RootState): number => selectViewStartingCount(get(state, 'orders.grid'));
export const selectOrdersGridViewEndingCount = (state: RootState): number => selectViewEndingCount(get(state, 'orders.grid'));
export const selectOrdersGridIsLoading = (state: RootState): boolean => selectIsLoading(get(state, 'orders.grid'));
export const selectOrdersGridIsGroupBysCountLoading = (state: RootState): boolean =>
  selectIsGroupBysCountLoading(get(state, 'orders.grid'));
export const selectOrdersGridisSidebarVisible = (state: RootState): boolean => selectisSidebarVisible(get(state, 'orders.grid'));
export const selectOrdersGridUserGridConfigurations = (state: RootState): OrdersGridState['userViewConfigurations'] =>
  selectUserGridConfigurations(get(state, 'orders.grid'));
export const selectOrdersGridOrderGridRateJobs = (state: RootState): OrdersGridRateJob[] =>
  selectOrderGridRateJobs(get(state, 'orders.grid'));
export const selectOrdersGridAlternativeRateJobs = (state: RootState): OrdersGridRateJob[] =>
  selectAlternativeRateJobs(get(state, 'orders.grid'));
export const selectOrdersGridActiveGroupByEnums = (state: RootState): OrdersGridActiveGroupBy[] =>
  selectActiveGroupByEnums(get(state, 'orders.grid'));
export const selectOrdersGridActiveGroupByCount = (state: RootState): OrdersGridGroupByCount[] =>
  selectActiveGroupByCount(get(state, 'orders.grid'));
export const selectOrdersGridHasGetOrdersFailure = (state: RootState): boolean => selectHasGetOrdersFailure(get(state, 'orders.grid'));
export const selectOrdersGridCurrentViewGridConfigOrDefault = (state: RootState, props: OrdersGridViewProps): OrdersGridColumnConfig[] =>
  selectCurrentViewGridConfigOrDefault(get(state, 'orders.grid'), props);
export const selectOrdersGridCurrentOrderByColumn = (state: RootState, props: OrdersGridViewProps): OrdersGridColumnConfig | undefined =>
  selectCurrentOrderByColumn(get(state, 'orders.grid'), props);
